import { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  MapPin,
  Sparkles,
  Check,
  ArrowRight,
  Phone,
  MessageCircle,
  X,
  ChevronLeft,
  ChevronRight,
  BadgeCheck,
} from "lucide-react";
import { PROJECTS, COMMON_PROJECT_BENEFITS } from "../lib/data";
import { COMPANY, buildWhatsAppLink, telLink } from "../lib/site";
import LeadForm from "../components/LeadForm";

export default function ProjectDetailPage({ onOpenLead }) {
  const { slug } = useParams();
  const project = PROJECTS.find((p) => p.slug === slug);
  const [lightbox, setLightbox] = useState(null);

  if (!project) {
    return <Navigate to="/projects" replace />;
  }

  const next = () =>
    setLightbox((i) => (i + 1) % project.gallery.length);
  const prev = () =>
    setLightbox((i) =>
      i === 0 ? project.gallery.length - 1 : i - 1
    );

  return (
    <div data-testid="project-detail-page">
      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-end overflow-hidden grain pt-32">
        <div className="absolute inset-0 z-0">
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/55 to-black/20" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/25 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-10 pb-16">
          <Link
            to="/projects"
            data-testid="back-to-projects"
            className="inline-flex items-center gap-2 text-white/60 hover:text-[#D4AF37] text-xs tracking-[0.25em] uppercase mb-10 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> All Projects
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 1 }}
            className="max-w-3xl"
          >
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <span className="text-[11px] tracking-[0.4em] uppercase text-[#D4AF37] font-medium">
                {project.category}
              </span>
              <span className="w-1.5 h-1.5 rounded-full bg-white/30" />
              <span className="flex items-center gap-1.5 text-[11px] tracking-[0.3em] uppercase text-white/60">
                <MapPin className="w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={1.6} />
                {project.location}
              </span>
            </div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-white font-light leading-[1.0]">
              {project.title}
            </h1>
            <div className="mt-8 inline-flex items-start gap-3 px-5 py-3.5 border border-[#D4AF37]/40 bg-[#D4AF37]/[0.08] backdrop-blur-sm">
              <Sparkles className="w-5 h-5 text-[#D4AF37] mt-0.5 shrink-0" strokeWidth={1.4} />
              <span className="text-[#F5E6CE] font-light tracking-wide">
                {project.highlight}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Overview + sidebar */}
      <section className="px-6 md:px-10 py-20 md:py-28">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-4">Overview</div>
            <p className="font-serif text-2xl md:text-3xl text-white/85 font-light leading-snug">
              {project.overview}
            </p>

            <div className="mt-14">
              <div className="eyebrow mb-6">Key Features</div>
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
                {project.benefits.map((b) => (
                  <li
                    key={b}
                    data-testid="project-feature"
                    className="flex items-start gap-3 text-white/85 font-light"
                  >
                    <Check className="w-5 h-5 text-[#D4AF37] mt-0.5 shrink-0" strokeWidth={1.6} />
                    {b}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-14">
              <div className="eyebrow mb-6">Backed by S Mahadeva Promise</div>
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
                {COMMON_PROJECT_BENEFITS.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-white/75 font-light text-sm">
                    <BadgeCheck className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" strokeWidth={1.6} />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="glass-strong p-8 md:p-10 lg:sticky lg:top-28">
              <div className="eyebrow mb-3">At a Glance</div>
              <h3 className="font-serif text-3xl text-white font-light mb-6">
                {project.title}
              </h3>
              <div className="space-y-5 mb-8">
                <Row label="Property Type" value={project.propertyType} />
                {project.startingPrice && (
                  <Row label="Starting Price" value={project.startingPrice} gold />
                )}
                {project.plotSize && <Row label="Plot Size" value={project.plotSize} />}
                {project.totalUnits && <Row label="Total Units" value={project.totalUnits} />}
                <Row label="Location" value={project.location} />
              </div>
              <button
                onClick={onOpenLead}
                data-testid="project-sidebar-cta"
                className="w-full inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black font-medium tracking-[0.2em] uppercase text-xs px-8 py-4 hover:bg-[#B3932E] transition-all duration-300 shadow-[0_0_25px_rgba(212,175,55,0.3)] hover:shadow-[0_0_45px_rgba(212,175,55,0.55)]"
              >
                {project.cta}
                <ArrowRight className="w-4 h-4" />
              </button>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <a
                  href={buildWhatsAppLink(
                    `Hello, I'm interested in ${project.title} at Dholera Smart City. Please share details.`
                  )}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="project-sidebar-whatsapp"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 border border-[#25D366]/60 text-[#25D366] text-[11px] tracking-[0.2em] uppercase hover:bg-[#25D366]/10 transition-all"
                >
                  <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.6} />
                  WhatsApp
                </a>
                <a
                  href={telLink(COMPANY.phones[1])}
                  data-testid="project-sidebar-call"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 border border-white/20 text-white text-[11px] tracking-[0.2em] uppercase hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                >
                  <Phone className="w-3.5 h-3.5" strokeWidth={1.6} />
                  Call
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="px-6 md:px-10 py-20 md:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="eyebrow mb-3">Gallery</div>
              <h2 className="font-serif text-4xl md:text-5xl text-white font-light">
                Inside <span className="gold-gradient-text italic">{project.title}</span>
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
            {project.gallery.map((src, i) => (
              <button
                key={src + i}
                onClick={() => setLightbox(i)}
                data-testid={`gallery-img-${i}`}
                className={`relative overflow-hidden group aspect-[4/3] ${
                  i === 0 ? "md:col-span-2 md:row-span-2 aspect-[4/5] md:aspect-auto" : ""
                }`}
              >
                <img
                  src={src}
                  alt={`${project.title} ${i + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms] ease-out"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-500" />
                <span className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] bg-black/70 px-3 py-1.5 transition-all duration-500">
                  View
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Inline lead form */}
      <section className="px-6 md:px-10 py-20 md:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-3">Enquire</div>
            <h2 className="font-serif text-4xl md:text-5xl text-white font-light leading-tight">
              Interested in{" "}
              <span className="gold-gradient-text italic">{project.title}</span>?
            </h2>
            <p className="mt-6 text-white/65 font-light">
              Our advisor will reach out within the next business hour with
              pricing, availability and a custom site-visit plan.
            </p>
          </div>
          <div className="lg:col-span-7 glass-strong p-8 md:p-10">
            <LeadForm
              testidPrefix="project-detail-lead"
              variant={`project-${project.slug}`}
              defaultMessage={`I'm interested in ${project.title} at Dholera Smart City.`}
            />
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          data-testid="lightbox"
          className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-xl flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
        >
          <button
            onClick={() => setLightbox(null)}
            data-testid="lightbox-close"
            className="absolute top-6 right-6 text-white/70 hover:text-[#D4AF37] transition-colors"
          >
            <X className="w-7 h-7" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            data-testid="lightbox-prev"
            className="absolute left-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-[#D4AF37] transition-colors"
          >
            <ChevronLeft className="w-10 h-10" />
          </button>
          <img
            src={project.gallery[lightbox]}
            alt=""
            onClick={(e) => e.stopPropagation()}
            className="max-w-[90vw] max-h-[85vh] object-contain"
          />
          <button
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            data-testid="lightbox-next"
            className="absolute right-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-[#D4AF37] transition-colors"
          >
            <ChevronRight className="w-10 h-10" />
          </button>
          <div className="absolute bottom-6 left-0 right-0 text-center text-xs tracking-[0.25em] uppercase text-white/60">
            {lightbox + 1} / {project.gallery.length}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, gold }) {
  return (
    <div className="flex justify-between items-baseline border-b border-white/5 pb-3">
      <span className="text-[10px] tracking-[0.3em] uppercase text-white/40">
        {label}
      </span>
      <span
        className={`font-light text-right ${
          gold ? "font-serif text-2xl text-[#D4AF37]" : "text-white"
        }`}
      >
        {value}
      </span>
    </div>
  );
}
